<template>
  <div id="pushDown">
    <div id="map">
      <b-loading :is-full-page="false" :active.sync="isLoading"></b-loading>
    </div>
    <div id="columnWrap">
      <div id="addColumn" class="col box animated fadeInDown faster" v-show="isDraw" v-bind:class="{'expanded-column':drawExists, 'collapsed-column':!drawExists}">
        <div id="addFormTitle" class="title is-5 field is-grouped is-grouped-centered">Add New Place</div>
        <div class="side-content" v-show="!drawExists" data-simplebar>
          <section>
            <div class="subtitle is-6">Use your cursor to draw an area around the hex(es) you would like to add. The area must contain the <strong>center</strong> of hexes you want to include. But don't worry, you can <i>edit</i> your selection once it's made!</div>
            <br>
            <div class="subtitle is-7">If your selection does not include any hex centroids, the last hex will be returned</div>
          </section>
        </div>
        <div class="side-content" v-show="drawExists" data-simplebar>
          <section>
            <div class="label">Edit Instructions</div>
            <div>Click on a hex in your selected area to <strong>remove</strong> it from your selection. Click on a hex next to your selected area to <strong>add</strong> it to your selection.</div>
            <br>
            <b-field label="New Place Name" message="What do you call this place?">
              <b-input v-model="newPlace"></b-input>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="newComment" maxlength="1000" type="textarea" placeholder="Optional: Add some context about this place"></b-input>
            </b-field>
            <div class="field is-grouped is-grouped-centered">
              <div class="control">
                <button id="addPlaceBtn" class="button is-primary" :disabled="newPlace == ''" @click="addNewPlace">Map It</button>
              </div>
            </div>
            <div class="field is-grouped is-grouped-centered">
              <div class="control">
                <a @click="cancelAdd">Cancel</a>
              </div>
            </div>
          </section>
        </div>
      </div>
      <div id="sidebarColumn" class="col expanded-column box animated faster" v-show="!drawExists" v-bind:class="{'expanded-column':!isDraw, 'collapsed-column':isDraw, 'fadeInUp':!isDraw, 'fadeInDown':isDraw}">
        <div id="sidebarTitle" class="title is-5 field is-grouped is-grouped-centered">Places</div>
        <div id="dataToggleWrap" class="has-text-centered noselect" @click="toggleVis" :class="{'no-click': disableVisToggle}">
          <div class="animated faster flipInX" v-show="visibilityToggle">
            <span class="user-icon-wrap">
              <i class="fas fa-2x fa-users user-icon" title="View only my places and comments"></i>
            </span>
            <span style="font-variant: small-caps; font-weight: bold">all entries</span>
          </div>
          <div class="animated faster flipInX" v-show="!visibilityToggle">
            <span class="user-icon-wrap">
              <i class="fas fa-2x fa-user user-icon" title="View all places and comments"></i>
            </span>
            <span style="font-variant: small-caps; font-weight: bold">your entries</span>
          </div>
        </div>
        <div class="subtitle is-6 side-content" v-if="topPlaces.length < 1" data-simplebar>When you mouse over a blue highlighted hex area, the top entries for that area will appear here</div>
        <div id="sideContent" class="side-content" data-simplebar>
          <div>
            <div v-for="place in topPlaces" v-if="topPlaces.length > 0 && specificPlaces.length < 1" class="place-card animated fadeIn faster has-text-centered">{{place.placeName}}</div>
            <b-collapse class="panel" :open="(specificPlaces.indexOf(place) === 0) ? true : false" v-show="specificPlaces.length > 0" :key="place.placeName" v-for="place in specificPlaces">
              <div slot="trigger" class="place-card has-text-centered">
                <strong>{{place.placeName}}</strong>
                <div class="is-size-7 is-username">added by: {{place.userName}} on {{convertDate(place.created)}}</div>
                <div class="edit-place-wrap" v-if="place.userId == user.id || user.isAdmin">
                  <i class="fas fa-pen edit-place" title="Edit Placename" @click="editPlace($event, place, specificPlaces.indexOf(place))"></i>
                </div>
              </div>
              <b-tabs v-model="activeTab[specificPlaces.indexOf(place)]" position="is-centered">
                <b-tab-item label="Comments">
                  <article v-if="place.comments" v-for="comment in place.comments" :key="comment.commentId" class="message is-primary">
                    <div class="message-body">
                      <div class="edit-wrap" v-if="comment.UserID == user.id || user.isAdmin">
                        <i class="fas fa-pen edit-comment" title="Edit Comment" @click="editComment(comment, specificPlaces.indexOf(place), place.comments.indexOf(comment))"></i>
                      </div>
                      {{comment.comment}}
                      <br>
                      <p><i class="is-size-7 is-pulled-right">{{ comment.user.name + " - " +  convertDate(comment.created_at) }}</i></p>
                    </div>
                  </article>
                  <article v-if="!place.comments" class="message is-primary">
                    <div class="message-body italic">Be the first to comment on this place!</div>
                  </article>
                </b-tab-item>
                <b-tab-item label="Add Comment">
                  <div class="has-text-centered">
                    <b-input v-model="indivComment" maxlength="1000" type="textarea" placeholder="New Comment"></b-input>
                    <a class="button is-primary" :disabled="indivComment === ''" :id="place.placeName" @click="addIndivComment(specificPlaces.indexOf(place), place)">Add Comment</a>
                  </div>
                </b-tab-item>
              </b-tabs>
            </b-collapse>
          </div>
        </div>
      </div>
    </div>
    <router-view />
    <b-modal :active.sync="commentModalActive" class="mobModal" :width="640">
      <div class="box">
        <b-field>
          <div class="has-text-centered is-size-5 title italic">Edit Your Comment</div>
        </b-field>
        <b-field>
          <b-input maxlength="1000" type="textarea" v-model="commentToEdit.comment"></b-input>
        </b-field>
        <div class="field is-grouped is-grouped-centered">
          <div class="control">
            <button class="button is-primary" @click="applyCommentEdits">Apply Edits</button>
          </div>
          <div class="control">
            <button class="button is-danger" @click="deleteComment">Delete</button>
          </div>
        </div>
        <div class="field is-grouped is-grouped-centered">
          <div class="control">
            <a @click="commentModalActive = false">Cancel</a>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal :active.sync="placeModalActive" class="mobModal" :width="640">
      <div class="box">
        <b-field>
          <div class="has-text-centered is-size-5 title italic">Edit Your Place</div>
        </b-field>
        <b-field>
          <b-input maxlength="255" type="textarea" v-model="placeToEdit.placeName"></b-input>
        </b-field>
        <div class="field is-grouped is-grouped-centered">
          <div class="control">
            <button class="button is-primary" @click="applyPlaceEdits">Apply Edits</button>
          </div>
          <div class="control">
            <button class="button is-danger" @click="deletePlace">Delete</button>
          </div>
        </div>
        <div class="field is-grouped is-grouped-centered">
          <div class="control">
            <a @click="placeModalActive = false">Cancel</a>
          </div>
        </div>
      </div>
    </b-modal>
    <div id="basemapToggle" @click="toggleBasemap"></div>
  </div>
</template>
<script>
import * as h3 from 'h3-js';
const h3Scale = 9;
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-selectareafeature';
import $ from 'jquery';
import router from "../router";
import {
  GeoSearchControl,
  OpenStreetMapProvider,
} from 'leaflet-geosearch';
import 'leaflet-geosearch/assets/css/leaflet.css';
import 'leaflet-geosearch/dist/style.css';
import SimpleBar from 'simplebar';
import 'simplebar/dist/simplebar.css';
export default {
  name: 'protectedMap',
  data() {
    return {
      map: {},
      imageryBasemap: {},
      streetsBasemap: {},
      currentBasemap: 0,
      drawLayerGroup: {},
      hexLayerGroup: {},
      hexPlacesLayerGroup: {},
      placesLayerGroup: {},
      activeTab: [],
      topPlaces: [],
      startPosition: {},
      specificPlaces: [],
      newPlace: "",
      newComment: "",
      selectedHex: "",
      addHexArray: [],
      uniqueHexes: [],
      addPlaceSuccessMsg: "",
      isBaseHex: null,
      commentPlace: "",
      indivComment: "",
      specificPlaceName: "",
      drawExists: false,
      isLoading: false,
      visibilityToggle: true,
      disableVisToggle: false,
      commentToEdit: {},
      commentModalActive: false,
      placeIndex: Number,
      commentIndex: Number,
      placeModalActive: false,
      placeToEdit: {},
      invisHexesStyle: {
        fillColor: "#9ecae1",
        weight: 4,
        opacity: 0,
        color: '#ffffcc',
        fillOpacity: 0
      },
      baseHexesStyle: {
        weight: 1,
        opacity: 1,
        color: 'darkgray',
        fillOpacity: 0,
        fillColor: 'whitesmoke'
      },
      placeHexesStyle: {
        fillColor: "#9ecae1",
        weight: 2,
        opacity: 1,
        color: '#3182bd',
        fillOpacity: 0.25
      },
      selectedHexStyle: {
        fillColor: "#9ecae1",
        weight: 4,
        opacity: 1,
        color: '#ffffcc',
        fillOpacity: 0.1
      }
    }
  },
  props: {
    open: Boolean,
    isDraw: Boolean
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    API_URL() {
      return this.$store.state.API_URL
    }
  },
  watch: {
    isDraw: function(newVal, oldVal) {
      var selectfeature = this.map.selectAreaFeature;
      selectfeature.options.color = '#ffd182';
      selectfeature.options.dashArray = '0, 0, 0, 0';
      selectfeature.options.weight = 5;
      let map = this.map

      // builds selected hex map layer with array of h3 hexes passed in
      let addDrawHexes = drawHexes => {
        var geojsonDrawHexes = {
          type: 'FeatureCollection',
          features: []
        };

        geojsonDrawHexes.features.push({
          type: 'Feature',
          // set the hexId property to the id of this hex
          properties: {
            "type": 'hex-outline'
          },
          geometry: {
            type: "Polygon",
            // use h3 to get the geometry for this hex
            coordinates: drawHexes
          }
        })

        var drawLayer = L.geoJson(geojsonDrawHexes, {
          pane: 'popupPane',
          style: {
            fillColor: "#ffd182",
            weight: 4,
            opacity: 1,
            color: '#ffd182',
            fillOpacity: 0.1
          },
          onEachFeature: (feature, layer) => {
            layer.on('mouseup', event => {
              let removeHex = h3.geoToH3(event.latlng.lat, event.latlng.lng, h3Scale);
              let index = this.addHexArray.indexOf(removeHex)
              this.addHexArray.splice(index, 1)
              if (this.addHexArray.length > 0) {
                let drawHexOutline = h3.h3SetToMultiPolygon(this.addHexArray, true);
                if(drawHexOutline.length > 1) {
                  // opent toast to warn not to create 2 separate areas
                  this.$toast.open({
                    duration: 3000,
                    message: `Can't delete this hex. Multiple hex area must be continuous`,
                    position: 'is-bottom',
                    type: 'is-danger'
                  })
                  this.addHexArray.push(removeHex)
                } else {
                  layer.remove()
                  addDrawHexes(drawHexOutline[0])
                }
              } else {
               this.$toast.open({
                  duration: 3000,
                  message: `Can't delete this hex. Cancel your drawing to start again`,
                  position: 'is-bottom',
                  type: 'is-danger'
                })
                this.addHexArray.push(removeHex)
              }
            })
            layer.on('hover', event => {
              //handle selected hex hover event
            })
          }
        });
        this.drawLayerGroup.clearLayers().addLayer(drawLayer);
      }

      // if the draw workflow is turned on
      if (newVal == true) {
        selectfeature.enable();
        this.map.off('click')
        this.map.off('contextmenu')

        // mouseup event handles drawing multiple select hex region
        this.map.on('mouseup', event => {
          // if no drawing/multiple hex selection exists yet
          if (this.drawExists == false) {
            var latlng_area = selectfeature.getAreaLatLng();
            let latlngArray = []
            latlng_area.forEach(pairObj => {
              let pairArray = []
              pairArray.push(pairObj.lng)
              pairArray.push(pairObj.lat)
              latlngArray.push(pairArray)
            })
            let drawHexArray = h3.polyfill(latlngArray, h3Scale, true);
            this.addHexArray = drawHexArray;

            // case where polyfill returns hexes ie. centroids were passed to h3
            if (drawHexArray.length > 0) {
              let drawHexOutline = h3.h3SetToMultiPolygon(drawHexArray, true);
              addDrawHexes(drawHexOutline[0]);
              selectfeature.removeAllArea();

              let sidebar = document.getElementById("sidebarColumn")
              sidebar.classList.remove("sidebar-expanded")
              sidebar.classList.add("sidebar-collapsed")

              this.drawExists = true;
              this.map.off('mouseup')
              selectfeature.disable();

            }
            // case where no hexes returned from polyfill call ie. no centroids
            // send lat long point from mouseup event to get single hex
            else {
              var getHex = h3.geoToH3(event.latlng.lat, event.latlng.lng, h3Scale);
              let drawHex = h3.h3ToGeoBoundary(getHex, true);
              this.addHexArray = [getHex];
              addDrawHexes([drawHex]);
              selectfeature.removeAllArea();

              this.drawExists = true;
              this.map.off('mouseup')
              selectfeature.disable();
            }
          }
        });
        // handle adding hex in edit workflow
        this.map.on('click', event => {
          if (this.drawExists) {
            let addHex = h3.geoToH3(event.latlng.lat, event.latlng.lng, h3Scale);
            function hasNeighbor (h3Hex) {
              return h3.h3IndexesAreNeighbors(addHex, h3Hex) === true;
            }
            function notDuplicate (h3Hex) {
              return h3Hex === addHex;
            }
            if(this.addHexArray.some(hasNeighbor)) {
              if(!this.addHexArray.some(notDuplicate)) {
                // add new area to the map with new hex included
                this.addHexArray.push(addHex)
                let drawHexOutline = h3.h3SetToMultiPolygon(this.addHexArray, true);
                addDrawHexes(drawHexOutline[0]);
              }
            }
          } else {
            this.map.off('click')
          }
          //this.hexLayerGroup.clearLayers();
          //this.hexPlacesLayerGroup.clearLayers();
        })
      }
      // if draw workflow is turned off
      else {
        this.drawLayerGroup.clearLayers();
        selectfeature.disable();
        this.newPlace = "";
        this.newComment = "";
        this.drawExists = false;
        this.map.off('click');
      }
    }
  },
  created() {
    if (this.user.id == "" || this.user.id == undefined || this.user.id == null) {
      router.push('/');
    }

    // $.get(this.API_URL + "/mob/checkauth")
    //   .done(data => {
    //     console.log('from protected', data)
    //   });

    //TODO: check actual passport session with another api call 
    //$.ajax(this.API_URL + "/mob/checkUser", {credentials: 'same-origin'})
  },
  mounted() {
    // update simplebar element on every sidebar click
    // make sure the size recalculates for scrolling
    let $sideContent = document.getElementById('sideContent');
    let recalcEl = new SimpleBar($sideContent)
    $sideContent.addEventListener('click', function() {
      setTimeout(function(){
        recalcEl.recalculate()
      }, 100);
    });

    let geoSuccess = position => {
      this.isLoading = true;
      this.startPosition = position;
      this.map.setView([this.startPosition.coords.latitude, this.startPosition.coords.longitude], 14)
    }
    let geoError = error => {
      console.log('geo-locate error', error)
      this.$toast.open({
        duration: 3000,
        message: `There was a problem obtaining your location`,
        position: 'is-bottom'
      })
    }

    navigator.geolocation.getCurrentPosition(geoSuccess, geoError);

    // set up the map after DOM is rendered
    const provider = new OpenStreetMapProvider();
    const searchControl = new GeoSearchControl({
      provider: provider
    });
    this.map = L.map('map', {
      attributionControl: false,
      minZoom: 2,
      maxBounds: [[85, -180], [-85, 180]]
    });
    this.map.on("movestart zoomstart", event => {
      this.isLoading = true;
    })
    this.map.on('moveend zoomend', event => {
      this.isLoading = false;
    })

    this.map.addControl(searchControl);

    const defaultCenter = [40.49, -74.439];
    const defaultZoom = 14;
    // this token has url restrictions and lives in the bright rain account, for now
    const mapboxToken = "pk.eyJ1IjoiYnJpZ2h0cmFpbiIsImEiOiJjanprMjMyMTMwNnV2M2RrYTc5MTU2NjRyIn0.OzUqxWoCHoRzlqxkH_vlMg";
    this.streetsBasemap = L.tileLayer('https://api.mapbox.com/styles/v1/brightrain/ck2b0qk130ccd1crsxnmt7q2c/tiles/256/{z}/{x}/{y}?access_token=' + mapboxToken, {
      attribution: 'Map data &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors, <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="http://mapbox.com">Mapbox</a>',
      maxZoom: 18,
      noWrap: true,
      bounds: [[85, -180], [-85, 180]],
      className: 'basemap',
    });
    // add streets basemap as default
    this.streetsBasemap.addTo(this.map);
    this.currentBasemap = 0;
    this.imageryBasemap = L.tileLayer('https://api.mapbox.com/styles/v1/brightrain/ck2cll1b100rq1cqsi6wee0nm/tiles/256/{z}/{x}/{y}?&access_token=pk.eyJ1IjoiYnJpZ2h0cmFpbiIsImEiOiJyMjgtNGk4In0.Y64dPMiS4Xi8BXRiDhWXyg', {
      attribution: 'Map data &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors, <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="http://mapbox.com">Mapbox</a>',
      maxZoom: 18,
      noWrap: true,
      bounds: [[85, -180], [-85, 180]],
      className: 'basemap',
    });

  /* ToDo: this was used to sandwich the hexes between the basemap and the streets\labels but
  was removed 2019-10 at the request of GG
  Leaving it here in case there is a change of <3
  (because I think it presents beautifully that way -dp)
    // create a map pane to hold label layer so we can set its z-index
    // working with map panes, good example of doing exactly this (sandwich geojson between base and labels)
    // https://leafletjs.com/examples/map-panes/
    this.map.createPane('labels');

    // We can set the order via zindex
    // defaults for layer z-index order in case we want to change anything
    // https://leafletjs.com/reference-1.3.2.html#map-pane
    // 625 between marker and tooltip
    this.map.getPane('labels').style.zIndex = 625;

    // Layers in this pane are non-interactive and do not obscure mouse/touch events
    this.map.getPane('labels').style.pointerEvents = 'none';

    // create and add the labels and streets layer
    // var mobTopLayer = L.tileLayer('https://api.mapbox.com/styles/v1/brightrain/cjk963ifh9gez2rpe75ofse60/tiles/256/{z}/{x}/{y}?access_token=' + mapboxToken, {
    //   pane: 'labels',
    //   noWrap: true,
    //   bounds: [[85, -180], [-85, 180]]
    // }).addTo(this.map);
  */
 
    const marker = L.marker(defaultCenter);

    let attribution = L.control.attribution({
      prefix: '<a href="https://leafletjs.com/">Leaflet</a> | <a href="https://uber.github.io/h3/#/">h3</a>',
      position: 'bottomleft'
    }).remove().addTo(this.map);

    attribution.removeAttribution('bottomright')

    // create the layer group that will hold our base hexes
    var hexLayerGroup = L.featureGroup([]).addTo(this.map);
    this.hexLayerGroup = hexLayerGroup;
    // create the layer group that will hold our defined places hexes
    var hexPlacesLayerGroup = L.featureGroup([]).addTo(this.map);
    this.hexPlacesLayerGroup = hexPlacesLayerGroup;
    var placesLayerGroup = L.featureGroup([]).addTo(this.map);
    this.placesLayerGroup = placesLayerGroup;
    // create empty layer group used for selecting multiple hexes
    var drawLayerGroup = L.featureGroup([]).addTo(this.map);
    this.drawLayerGroup = drawLayerGroup;

    this.map.setView(defaultCenter, defaultZoom);

    // add the hexes to the current extent
    var currentExtent = this.getCurrentExtent();
    // get the hexes (ids) that are contained in the current extent at the set scale
    var hexes = h3.polyfill(currentExtent, h3Scale, true);

    // the layer used to hold the hexes
    this.map.on('moveend', e => {
      this.uniqueHexes = [];
      // only build and display hexes if zoomed in to a set level, or greater
      let zoom = this.map.getZoom();
      //refreshLayers();
      if (zoom > 12) {
        var currentExtent = this.getCurrentExtent();
        var hexes = h3.polyfill(currentExtent, h3Scale, true);
        // get the hexes for the entire current extent
        this.addBaseHexes(hexes);
        // get the hexes of our defined places
        if(this.visibilityToggle) {
          this.addHiddenHexes(hexes);
        } else {
          this.getUserHexes(hexes)
        }
        this.placesLayerGroup.bringToFront();
        bringSelectedToFront();
        this.drawLayerGroup.bringToFront();
        this.disableVisToggle = false;
      } else {
        this.disableVisToggle = true;
        hexLayerGroup.clearLayers();
        hexPlacesLayerGroup.clearLayers();
      }
    });

    let bringSelectedToFront = () => {
      if (this.isBaseHex) {
        let topLevelLayerObj = hexLayerGroup._layers[Object.keys(hexLayerGroup._layers)[0]];
        let baseHexLayer = topLevelLayerObj._layers;
        for (var key in baseHexLayer) {
          if (baseHexLayer[key].feature.properties.hexId == this.selectedHex) {
            baseHexLayer[key].bringToFront()
          }
        }
      }
    }

    // these must go at the bottom to use arrow functions to define them
    this.addBaseHexes(hexes);
    this.addHiddenHexes(hexes);
    this.getPlaces();

    let $dataToggle = document.getElementById('dataToggleWrap');

    $dataToggle.addEventListener('click', () => {
      this.refreshLayers();
      this.topPlaces = [];
    });

  },
  methods: {
    addNewPlace() {
      var place = this.newPlace;
      var placeData = { 
        placename: place,
        userID: this.user.id,
        hexArray: this.addHexArray
      };

      $.ajax(this.API_URL + "/mob/addplace", {
        data: JSON.stringify(placeData),
        contentType: 'application/json',
        type: 'POST',
        success: (data) => {
          this.addComment(data.id[0]);
        },
        error: (data) => {
          console.log(data)
          this.$toast.open({
            duration: 5000,
            message: `There was a problem adding this place. Please try again`,
            position: 'is-bottom',
            type: 'is-danger'
          })
        }
      })
    },
    convertDate (inDate) {
      var options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(inDate).toLocaleDateString("en-US", options);
    },
    addIndivComment(index, place) {
      var placeData = {
        placeID: this.specificPlaces[index].placeId,
        userID: this.user.id,
        comment: this.indivComment
      };

      $.ajax(this.API_URL + "/mob/addcomment", {
        data: JSON.stringify(placeData),
        contentType: 'application/json',
        type: 'POST',
        success: (newCommentId) => {
          // add a dummy to the places' comments so it will show in the list without refresh
          let newComment = {
            "CommentID": newCommentId.response[0],
            "comment": this.indivComment,
            "created_at": new Date().toLocaleDateString(),
            "PlaceID": place.placeId,
            "UserID": this.user.id,
            "user": {
              "name": this.user.name
            }
          }
          if(this.specificPlaces[index].comments) {
            this.specificPlaces[index].comments.push(newComment);
          } else {
            this.specificPlaces[index].comments = [newComment]
          }
          this.indivComment = "";
          this.activeTab[index] = 0;
          let $sideContent = new SimpleBar(document.getElementById('sideContent'));
          $sideContent.recalculate()
        }
      })
    },
    cancelAdd() {
      var selectfeature = this.map.selectAreaFeature;
      selectfeature.disable();
      this.drawLayerGroup.clearLayers();
      this.$emit('activateDraw')
      this.drawExists = false
    },
    toggleVis() {
      this.visibilityToggle = !this.visibilityToggle
    },
    editComment(comment, placeIndex, commentIndex) {
      this.commentModalActive = true;
      this.commentToEdit = comment;
      this.placeIndex = placeIndex;
      this.commentIndex = commentIndex;
    },
    editPlace(event, place, placeIndex) {
      event.stopPropagation();
      this.placeModalActive = true;
      this.placeToEdit = place;
      this.placeIndex = placeIndex;
    },
    addComment(placeId) {
      if(this.newComment != "") {
        var commentData = {
          userID: this.user.id,
          placeID: placeId,
          comment: this.newComment           
        }
        $.ajax(this.API_URL + "/mob/addcomment", {
          data: JSON.stringify(commentData),
          contentType: 'application/json',
          type: 'POST',
          success: (data) => {
            this.refreshMap();
            // show details of place just added
            if(this.visibilityToggle) {
              this.getHexPlaces(this.addHexArray[0]);
            } else {
              this.getUserHexPlaces(this.addHexArray[0]);
            }
            
            this.$toast.open({
              duration: 3000,
              message: `New place added successfully!`,
              position: 'is-bottom',
              type: 'is-success'
            })
          },
          error: (data) => {
            console.log(data)
            this.$toast.open({
              duration: 5000,
              message: `There was a problem adding your comment.`,
              position: 'is-bottom',
              type: 'is-danger'
            })
          }
        })
      } else {
        this.refreshMap();
        // show details of place just added
        if(this.visibilityToggle) {
          this.getHexPlaces(this.addHexArray[0]);
        } else {
          this.getUserHexPlaces(this.addHexArray[0]);
        }
        this.$toast.open({
          duration: 3000,
          message: `New place added successfully!`,
          position: 'is-bottom',
          type: 'is-success'
        })
      }
    },
    deleteComment() {
      this.$dialog.confirm({
        message: 'Are you sure you want to delete this comment? This cannot be undone.',
        type: 'is-danger',
        onConfirm: () => {
          let commentData = {
            commentId: this.commentToEdit.CommentID
          }
          $.ajax(this.API_URL + "/mob/deletecomment", {
            data: JSON.stringify(commentData),
            contentType: 'application/json',
            type: 'POST',
            success: data => {
              this.$toast.open({
                message: 'Comment Deleted!',
                type: 'is-success'
              })
              this.commentModalActive = false;
              this.specificPlaces[this.placeIndex].comments.splice(this.commentIndex, 1)
            }
          });
        }
      })
    },
    applyCommentEdits() {
      let editData = {
        commentId: this.commentToEdit.CommentID,
        commentText: this.commentToEdit.comment
      }
      $.ajax(this.API_URL + "/mob/editcomment", {
        data: JSON.stringify(editData),
        contentType: 'application/json',
        type: 'POST',
        success: newCommentText => {
          this.$toast.open({
            message: 'Comment Updated',
            type: 'is-success'
          })
          this.commentModalActive = false;
          this.specificPlaces[this.placeIndex].comments[this.commentIndex].comment = newCommentText[0].comment
          let $sideContent = new SimpleBar(document.getElementById('sideContent'));
          $sideContent.recalculate()
        }
      });
    },
    applyPlaceEdits() {
      let editData = {
        placeId: this.placeToEdit.placeId,
        placeText: this.placeToEdit.placeName
      }
      $.ajax(this.API_URL + "/mob/editplace", {
        data: JSON.stringify(editData),
        contentType: 'application/json',
        type: 'POST',
        success: newPlaceText => {
          this.$toast.open({
            message: 'Place Updated',
            type: 'is-success'
          })
          this.placeModalActive = false;
          this.specificPlaces[this.placeIndex].placeName = newPlaceText[0].PlaceName
          let $sideContent = new SimpleBar(document.getElementById('sideContent'));
          $sideContent.recalculate()
        }
      });
    },
    deletePlace() {
      this.refreshLayers()
      this.$dialog.confirm({
        message: 'Are you sure you want to delete this Place? This cannot be undone and will delete all of its associated comments.',
        type: 'is-danger',
        onConfirm: () => {
          let placeData = {
            placeId: this.placeToEdit.placeId
          }
          this.placeModalActive = false;
          $.ajax(this.API_URL + "/mob/deleteplace", {
            data: JSON.stringify(placeData),
            contentType: 'application/json',
            type: 'POST',
            success: data => {
              this.$toast.open({
                message: 'Place Deleted!',
                type: 'is-success'
              })
              this.specificPlaces[this.placeIndex]
              // TODO: update blue hex layer on map
              this.refreshLayers();
            }
          });
        }
      })
    },
    getCurrentExtent() {
      // build the current exent that can be passed to h3 (in h3 useable format) to get the hexes within
      let currentExtent = [];
      currentExtent.push([this.map.getBounds().getNorthWest().lng, this.map.getBounds().getNorthWest().lat]);
      currentExtent.push([this.map.getBounds().getNorthEast().lng, this.map.getBounds().getNorthEast().lat]);
      currentExtent.push([this.map.getBounds().getSouthEast().lng, this.map.getBounds().getSouthEast().lat]);
      currentExtent.push([this.map.getBounds().getSouthWest().lng, this.map.getBounds().getSouthWest().lat]);
      return currentExtent;
    },
    addPlacesHexLayer(places) {
      var geojsonPlaces = {
        type: 'FeatureCollection',
        features: []
      };
      for (let i = 0; i < places.length; i++) {
        let array = [];
        for (let j = 0; j < places[i].placehexes.length; j++) {
          array.push(places[i].placehexes[j].HexID)
        }
        let hexes = h3.h3SetToMultiPolygon(array, true);
        let finishedHexes = hexes[0][0]
        geojsonPlaces.features.push({
          type: 'Feature',
          // set the hexId property to the id of this hex
          properties: [{
            "placeName": places[i].PlaceName,
            "hexes": places[i].placehexes,
            "userid": places[i].UserID,
            "comments": places[i].comments
          }],
          geometry: {
            type: "Polygon",
            // use h3 to get the geometry for this hex
            coordinates: [finishedHexes]
          }
        })
      }
      var placeLayer = L.geoJson(geojsonPlaces, {
        style: this.placeHexesStyle,
        onEachFeature: (feature, layer) => {
          layer.on('click', () => {
            // cant fire because it's behind all other layers
          })
        }
      });
      this.placesLayerGroup.clearLayers().addLayer(placeLayer);
      this.placesLayerGroup.on('mouseover', () => {
        if (this.selectedHex == "" && !this.isDraw) {
          this.placesLayerGroup.bringToBack()
        }
      })
      this.placesLayerGroup.on('mouseout', () => {
        if (this.selectedHex == "" && !this.isDraw) {
          this.placesLayerGroup.bringToFront()
        }
      })
    },
    addHiddenHexLayer(hexes) {
      this.uniqueHexes = []
      // build the (empty) geojson feature collection
      var geojsonHexes = {
        type: 'FeatureCollection',
        features: []
      };
      let selected = {};
      // create a new geojson feature for each hex and push it in the geojson features array
      hexes.forEach(hex => {
        if (this.uniqueHexes.includes(hex.HexID)) {
          let multiplePlaceHex = geojsonHexes.features.filter(jsonHex => {
            return jsonHex.properties[0].hexId == hex.HexID
          })
          multiplePlaceHex[0].properties.push({
            "placeName": hex.PlaceName,
            "hexId": hex.HexID,
            "userid": hex.UserID
          })
        } else {
          this.uniqueHexes.push(hex.HexID)
          geojsonHexes.features.push({
            type: 'Feature',
            // set the hexId property to the id of this hex
            properties: [{
              "placeName": hex.PlaceName,
              "hexId": hex.HexID,
              "userid": hex.UserID
            }],
            geometry: {
              type: "Polygon",
              // use h3 to get the geometry for this hex
              coordinates: [h3.h3ToGeoBoundary(hex.HexID, true)]
            }
          })
        }
      });

      // add hexLayer to the map using the geojson we just created
      // invis version of blue hexes 
      var hiddenHexLayer = L.geoJson(geojsonHexes, {
        style: this.invisHexesStyle,
        onEachFeature: (feature, layer) => {
          if (feature.properties[0].hexId == this.selectedHex) {
            layer.setStyle(this.selectedHexStyle);
          }
          if (feature.properties) {
            layer.on('mouseover', () => {
              if (this.selectedHex == "" && !this.isDraw) {
                this.topPlaces = [];
                for (let i = 0; i < feature.properties.length; i++) {
                  this.topPlaces.push({ 'placeName': feature.properties[i].placeName })
                }
                layer.bringToFront();
                layer.setStyle(this.selectedHexStyle);
              }
            });
            layer.on('mouseout', () => {
              if (this.selectedHex == "" && !this.isDraw) {
                layer.setStyle(this.invisHexesStyle);
              }
            });
            layer.on('click', event => {
              if(!this.isDraw) {
                if (this.selectedHex != feature.properties[0].hexId) {
                  this.hexPlacesLayerGroup.bringToFront();
                  this.isBaseHex = false;
                  this.hexLayerGroup.setStyle(this.baseHexesStyle);
                  this.hexPlacesLayerGroup.setStyle(this.invisHexesStyle);
                  let feature = event.target.feature.properties;
                  if(this.visibilityToggle) {
                    this.getHexPlaces(feature[0].hexId);
                  } else {
                    this.getUserHexPlaces(feature[0].hexId)
                  }
                  layer.setStyle(this.selectedHexStyle);
                  this.selectedHex = feature[0].hexId;
                  this.addHexArray = [];
                  this.addHexArray.push(feature[0].hexId)
                } else if(this.selectedHex == feature.properties[0].hexId && this.isDraw){
                  // case where blue hex is selected when draw tool is launched and same hex is selected for drawing
                  // keep this.addHexArray the same
                  return
                } else {
                  this.isBaseHex = null;
                  hiddenHexLayer.setStyle(this.invisHexesStyle)
                  this.selectedHex = '';
                  layer.setStyle(this.placeHexesStyle);
                  this.specificPlaces = [];
                  this.addHexArray = [];
                  this.activeTab = [];
                }
              }
            });
          }
        }
      });
      // remove existing place hexes and add the new\updated ones here
      this.hexPlacesLayerGroup.clearLayers().addLayer(hiddenHexLayer);
      this.hexPlacesLayerGroup.setZIndex(10)
    },
    addBaseHexes(hexes) {
      // build the (empty) geojson feature collection
      var geojsonHexes = {
        type: 'FeatureCollection',
        features: []
      };

      // create a new geojson feature for each hex and push it in the geojson features array
      hexes.forEach(hex => {
        geojsonHexes.features.push({
          type: 'Feature',
          // set the hexId property to the id of this hex
          properties: {
            "hexId": hex
          },
          geometry: {
            type: "Polygon",
            // use h3 to get the geometry for this hex
            coordinates: [h3.h3ToGeoBoundary(hex, true)]
          }
        })
      });
      // add hexLayer to the map using the geojson we just created and the passed style
      // base hexes
      var hexLayer = L.geoJson(geojsonHexes, {
        style: this.baseHexesStyle,
        onEachFeature: (feature, layer) => {
          layer.on('click', event => {
            if(!this.drawExists) {
              this.isBaseHex = null;
              this.hexPlacesLayerGroup.setStyle(this.invisHexesStyle)
              this.selectedHex = '';
              //layer.setStyle(placeHexesStyle);
              this.addHexArray = [];
              this.specificPlaces = [];
              this.activeTab = [];
            } else {
              // draw is active
              layer.setStyle(this.invisHexesStyle);
              return
            }
            
          });
          layer.on('mouseover', item => {
            if(!this.drawExists) {
              if (this.selectedHex == "") {
                layer.bringToFront();
                layer.setStyle(this.selectedHexStyle);
                this.topPlaces = [];
              }
            } else {
              // draw is active
              return
            }
          });
          layer.on('mouseout', item => {
            if(!this.drawExists) {
              if (this.selectedHex == "") {
                layer.setStyle(this.baseHexesStyle);
                layer.bringToBack()
              }
            } else {
              // draw is active
              return
            }
          });
        }
      });
      // clear current hexes and add these here new ones
      this.hexLayerGroup.clearLayers().addLayer(hexLayer);

      // look thru hexLayerGroup layers and find the selected hex
      // bring selected hex to front after whole layer is added
      // set selected hex style
      let topLevelLayerObj = this.hexLayerGroup._layers[Object.keys(this.hexLayerGroup._layers)[0]];
      let baseHexLayer = topLevelLayerObj._layers;
      for (var key in baseHexLayer) {
        if (baseHexLayer[key].feature.properties.hexId == this.selectedHex) {
          baseHexLayer[key].setStyle(this.selectedHexStyle);
          baseHexLayer[key].bringToFront()
        }
      }
    },
    getPlaces() {
      $.ajax(this.API_URL + "/mob/getplaces", {
        contentType: 'application/json',
        type: 'GET',
        success: data => {
          this.addPlacesHexLayer(data);
        }
      })
    },
    addHiddenHexes(hexes) {
      $.ajax(this.API_URL + "/mob/gethexes", {
        data: JSON.stringify(hexes),
        contentType: 'application/json',
        type: 'POST',
        success: data => {
          this.addHiddenHexLayer(data);
        }
      });
    },
    getUserPlaces() {
      $.ajax(this.API_URL + "/mob/getplaces/" + this.user.id, {
        contentType: 'application/json',
        type: 'POST',
        success: data => {
          this.addPlacesHexLayer(data);
        }
      })
    },
    getUserHexes(hexes) {
      // fires on blue hex click
      $.ajax(this.API_URL + "/mob/gethexes/" + this.user.id, {
        data: JSON.stringify(hexes),
        contentType: 'application/json',
        type: 'POST',
        success: data => {
          this.addHiddenHexLayer(data);
        }
      })
    },
    getUserHexPlaces(hexId) {
      // fires on blue hex click
      let hexData = {
        hexId: hexId
      }
      $.ajax(this.API_URL + "/mob/gethexplaces/" + this.user.id, {
        data: JSON.stringify(hexData),
        contentType: 'application/json',
        type: 'POST',
        success: data => {
          this.specificPlaces = []
          this.activeTab = []
          data.forEach(place => {
            if(place.comments.length > 0) {
              this.specificPlaces.push({
                'placeName': place.PlaceName,
                'userName': place.user.name,
                'comments': place.comments,
                'placeId': place.PlaceID,
                'userId': place.UserID
              })
            } else {
              this.specificPlaces.push({
                'placeName': place.PlaceName,
                'userName': place.user.name,
                'placeId': place.PlaceID,
                'userId': place.UserID
              })
            }
            this.activeTab.push(0)
          })
        }
      })
    },
    getHexPlaces(hexId) {
      $.ajax(this.API_URL + "/mob/get/gethexplaces/" + hexId, {
        contentType: 'application/json',
        type: 'GET',
        success: data => {
          this.specificPlaces = []
          this.activeTab = []
          data.forEach(place => {
            if(place.comments.length > 0) {
              this.specificPlaces.push({
                'placeName': place.PlaceName,
                'userName': place.user.name,
                'comments': place.comments,
                'placeId': place.PlaceID,
                'userId': place.UserID,
                'created': place.created_at
              })
            } else {
              this.specificPlaces.push({
                'placeName': place.PlaceName,
                'userName': place.user.name,
                'placeId': place.PlaceID,
                'userId': place.UserID,
                'created': place.created_at
              })
            }
            this.activeTab.push(0)
          })
        }
      });
    },
    refreshLayers() {
      if(this.map.getZoom() > 12) {
        let currentExtent = this.getCurrentExtent();
        let hexes = h3.polyfill(currentExtent, h3Scale, true);
        if(!this.visibilityToggle) {
          this.isBaseHex = null;
          this.selectedHex = '';
          this.addHexArray = [];
          this.specificPlaces = [];
          this.activeTab = [];

          this.hexPlacesLayerGroup.eachLayer(layer => {
            layer.setStyle(this.invisHexesStyle)
          });

          this.getUserPlaces()
          this.getUserHexes(hexes)
        } else {
          this.selectedHex = '';
          this.addHexArray = [];
          this.specificPlaces = [];
          this.activeTab = [];

          this.hexPlacesLayerGroup.eachLayer(layer => {
            layer.setStyle(this.invisHexesStyle)
          });

          this.getPlaces();
          this.addHiddenHexes(hexes);
        }
      } else {
        return
      }
    },
    refreshMap() {
      this.addPlaceSuccessMsg = this.newPlace + " was successfully added!"
      this.newComment = "";
      this.newPlace = "";
      let currentExtent = this.getCurrentExtent();
      let hexes = h3.polyfill(currentExtent, h3Scale, true);
      this.addBaseHexes(hexes);
      
      if(this.visibilityToggle) {
        this.getPlaces();
        this.addHiddenHexes(hexes);
      } else {
        this.getUserPlaces()
        this.getUserHexes(hexes)
      }
      
      this.placesLayerGroup.bringToFront();
      this.drawExists = false;
      this.drawLayerGroup.clearLayers();
      this.$emit('activateDraw');
    },
    toggleBasemap() {
      //console.log('toggle basemap')
      if(this.currentBasemap === 0) {
        this.map.eachLayer(layer => {
          if(layer.options.className === 'basemap') {
            this.map.removeLayer(layer)
          } else {
            return
          }
        })
        this.imageryBasemap.addTo(this.map);
        this.currentBasemap = 1;
      } else {
        this.map.eachLayer(layer => {
          if(layer.options.className === 'basemap') {
            this.map.removeLayer(layer)
          } else {
            return
          }
        })
        this.streetsBasemap.addTo(this.map);
        this.currentBasemap = 0;
      }
    }
  }
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@mixin margin-auto {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
}
#pushDown {
  height: calc(100% - 91px);
  width: 100%;
  position: absolute;
  top: 71px;
  overflow-y: hidden !important;
}
.edit-wrap {
  position: relative;
  color: #2870a3;
}
.edit-place-wrap {
  position: relative;
  color: #363636;
}
.edit-comment {
  position: absolute;
  top: -15px;
  right: -18px;
  cursor: pointer;
}
.edit-place {
  position: absolute;
  top: -38px;
  right: -8px;
  cursor: pointer;
}
#dataToggleWrap {
  font-family: "HelveticaNeue-Light", "Helvetica Neue Light", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif;
  font-size: 16px;
  background-color: #f7fafd;
  padding: 18px 15px;
  cursor: pointer;
}
#dataToggleWrap:hover {
  background-color: #ffffef;
}
.user-icon-wrap {
  position: absolute;
  height: 15px !important;
  width: 15px !important;
  left: 0px;
  top: -7px;
  color: #3182bd;
}
.user-icon {
  position: relative;
  float: left;
  top: 3px;
}
.noselect {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.no-click {
  opacity: 0.5;
  pointer-events: none;
}
.panel:not(:last-child) {
  margin-bottom: 1rem !important;
}
.message {
  width: 100%;
}
#map {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 1;
  left: 0;
}
.col {
  padding: 0px;
  margin-bottom: 10px;
  overflow: hidden;
  .title {
    position: relative;
    width: 100%;
    padding: 15px;
    margin-bottom: 0px;
    cursor: default;
  }
  #sidebarTitle {
    background-color: #363636;
    color: whitesmoke;
    cursor: default;
  }
  #addFormTitle {
    background-color: #ffd182;
    color: rgba(0,0,0,0.7);
    cursor: default;
  }
  .side-content {
    font-size: 14px;
    line-height: 17px;
    padding: 10px 15px;
    margin: 0px;
    height: calc(100% - 112px);
    overflow: auto;
    cursor: default;
  }
}
.expanded-column {
  height: 96%;
}
.collapsed-column {
  height: 48%;
}

#columnWrap {
  position: absolute;
  font-family: "HelveticaNeue-Light", "Helvetica Neue Light", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif;
  width: 25%;
  right: 0px;
  height: calc(100% - 92px);
  margin: 5px;
  overflow: hidden;
  z-index: 99999;
}
.place-card {
  background-color: #ffffef;
  border-radius: 4px 4px 0 0;
  color: #363636;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  padding: 0.5em 0.75em;
  border: 1px solid #dbdbdb;
  margin-bottom: 1rem;
}
.is-username {
  font-weight: 400;
  font-style: italic;
  text-align: center;
  line-height: 12px;
}
.margin-top {
  margin-top: 10px;
}
.comment-collapse {
  width: 100%;
}
.not-bold {
  font-weight: 400 !important;
  font-style: italic;
}
.italic {
  font-style: italic;
  font-weight: 320;
}
.no-padding {
  padding: 0px !important;
}
.mobModal {
  z-index: 999999 !important;
}
#basemapToggle {
  background-image: url('../assets/layers.png');
  background-color: #fff;
  background-size: 37px 37px;
  background-position: center;
  width: 34px;
  height: 34px;
  position: absolute;
  left: 10px;
  top: 128px;
  z-index: 999;
  border-radius: 4px;
  line-height: 26px;
  border: 2px solid rgba(0,0,0,0.2);
  background-clip: padding-box;
}
#basemapToggle:hover {
  background-color: #f4f4f4;
  cursor: pointer;
}
</style>